import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  OverviewV2ApiError,
  PaidMediaOverviewV2ApiFactory,
  type OverviewV2BaseParams,
  type OverviewV2ContextParams,
} from "@/apis/brand-os/paid-media-overview-v2";
import type {
  OverviewV2ExternalSignalsResponse,
  OverviewV2SnapshotResponse,
  OverviewV2SummaryResponse,
  OverviewV2TopMoversResponse,
} from "@/schema/brand-os/paid-media-overview-v2";

/**
 * Loads the combined overview payload and exposes per-section "refresh now"
 * callbacks that bypass the backend cache.
 *
 * Refresh strategy:
 *   - Each section's refresh button calls the dedicated section endpoint with
 *     `?refresh=true`. The result is written back into the summary cache
 *     under the same key, so the page rerenders the freshly-served slice
 *     without a full-page reload.
 *   - We also surface a per-section `refreshing` boolean so the section
 *     header can show a spinning icon without the whole page going into a
 *     loading state (the rest of the data is still fresh).
 *
 * NOTE: the bulk fetch is rate-limited by the backend (60–120s cold). We
 * intentionally disable React Query auto-retries (`retry: false` in the
 * factory) — auto-retry would multiply LLM cost. The page surfaces a manual
 * retry button instead.
 */
export function usePaidMediaOverviewV2(params: OverviewV2ContextParams) {
  const queryClient = useQueryClient();
  const summaryKey = PaidMediaOverviewV2ApiFactory.Keys.Summary(params);
  const summaryQuery = useQuery(PaidMediaOverviewV2ApiFactory.Queries.FetchSummary(params));

  /**
   * Patches the cached summary so a freshly-refreshed section slice replaces
   * the stale one inline. Also clears the matching entry in `errors` so a
   * previously-failed section that just succeeded stops showing its error
   * affordance.
   */
  const patchSummary = useCallback(
    (
      section: "snapshot" | "top_movers" | "external_signals",
      next:
        | OverviewV2SnapshotResponse
        | OverviewV2TopMoversResponse
        | OverviewV2ExternalSignalsResponse,
    ) => {
      queryClient.setQueryData<OverviewV2SummaryResponse | undefined>(
        summaryKey,
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            [section]: next,
            // Reset `synced_at` to mirror the backend's behaviour when a
            // single section is refreshed — gives the "last updated" chip
            // an honest timestamp.
            synced_at: next.synced_at ?? prev.synced_at,
            errors: prev.errors.filter((e) => e.section !== section),
          };
        },
      );
    },
    [queryClient, summaryKey],
  );

  // Per-section refresh mutations. We pass through the same context params so
  // the URL params line up with what the backend used for the original fetch.
  const snapshotRefresh = useSectionRefresh({
    section: "snapshot",
    fetcher: (p) => PaidMediaOverviewV2ApiFactory.Apis.FetchSnapshot(p),
    params,
    onSuccess: (data) => patchSummary("snapshot", data),
  });
  const topMoversRefresh = useSectionRefresh({
    section: "top_movers",
    fetcher: (p) => PaidMediaOverviewV2ApiFactory.Apis.FetchTopMovers(p),
    params,
    onSuccess: (data) => patchSummary("top_movers", data),
  });
  const externalSignalsRefresh = useSectionRefresh<
    OverviewV2ContextParams,
    OverviewV2ExternalSignalsResponse
  >({
    section: "external_signals",
    fetcher: (p) => PaidMediaOverviewV2ApiFactory.Apis.FetchExternalSignals(p),
    params,
    onSuccess: (data) => patchSummary("external_signals", data),
  });

  // Page-level "Refetch all". We can't just call `summaryQuery.refetch()` —
  // that re-runs with the original params (no `refresh`), so it hits the same
  // cache slot and the backend short-circuits to its 24h cached payload. We
  // run a dedicated mutation with `refresh: true` and write the fresh result
  // back into the existing summary cache slot so the page rerenders without
  // tearing down/remounting sections.
  const refreshAllMutation = useMutation({
    mutationKey: ["paid-media-overview-v2", "refresh", "all"],
    mutationFn: () =>
      PaidMediaOverviewV2ApiFactory.Apis.FetchSummary({ ...params, refresh: true }),
    onSuccess: (data) => {
      queryClient.setQueryData<OverviewV2SummaryResponse | undefined>(summaryKey, data);
    },
  });
  const refreshAll = useCallback(() => refreshAllMutation.mutate(), [refreshAllMutation]);

  return {
    summary: summaryQuery.data,
    isLoading: summaryQuery.isLoading,
    isFetching: summaryQuery.isFetching || refreshAllMutation.isPending,
    error: summaryQuery.error as OverviewV2ApiError | Error | null,
    refetch: summaryQuery.refetch,
    refreshAll,
    snapshot: { refresh: snapshotRefresh.refresh, refreshing: snapshotRefresh.isPending },
    topMovers: { refresh: topMoversRefresh.refresh, refreshing: topMoversRefresh.isPending },
    externalSignals: {
      refresh: externalSignalsRefresh.refresh,
      refreshing: externalSignalsRefresh.isPending,
    },
  };
}

interface UseSectionRefreshOpts<P extends OverviewV2BaseParams, T> {
  section: string;
  fetcher: (params: P) => Promise<T>;
  params: P;
  onSuccess: (data: T) => void;
}

function useSectionRefresh<P extends OverviewV2BaseParams, T>(
  opts: UseSectionRefreshOpts<P, T>,
) {
  // We bake `refresh: true` into the call here rather than at the call
  // site so the section components stay agnostic of the cache-bypass mechanic.
  const [lastError, setLastError] = useState<OverviewV2ApiError | Error | null>(null);
  const mutation = useMutation({
    mutationKey: ["paid-media-overview-v2", "refresh", opts.section],
    mutationFn: () => opts.fetcher({ ...opts.params, refresh: true }),
    onSuccess: (data) => {
      setLastError(null);
      opts.onSuccess(data);
    },
    onError: (err) => {
      setLastError(err as OverviewV2ApiError | Error);
    },
  });

  return {
    refresh: () => mutation.mutate(),
    isPending: mutation.isPending,
    error: lastError,
  };
}
